const TAGGED_KEY_PATTERN = /^(\d+)(?:\.|$)/;

const tagFromDetailedKey = (key) => {
  const match = TAGGED_KEY_PATTERN.exec(key);
  if (!match) {
    return null;
  }

  const tag = Number(match[1]);
  return Number.isSafeInteger(tag) ? tag : null;
};

/**
 * Sorts detailed KLV keys by their numeric tag. Untagged fields are kept
 * after tagged fields and ordered by name so mixed local sets remain stable.
 */
export const compareDetailedKlvKeys = (keyA, keyB) => {
  const tagA = tagFromDetailedKey(keyA);
  const tagB = tagFromDetailedKey(keyB);

  if (tagA !== null && tagB !== null) {
    return tagA - tagB || keyA.localeCompare(keyB);
  }
  if (tagA !== null) {
    return -1;
  }
  if (tagB !== null) {
    return 1;
  }
  return keyA.localeCompare(keyB);
};

// Run once when preparing a displayed packet, never from the tree renderer.
// Already ordered objects are returned unchanged; values and arrays keep their
// identity unless a descendant actually needs reordering.
export function sortKlvForDisplay(value) {
  if (Array.isArray(value)) {
    const children = value.map(sortKlvForDisplay);
    return children.every((child, index) => child === value[index]) ? value : children;
  }
  if (value === null || typeof value !== "object") return value;
  const entries = Object.entries(value).map(([key, child]) => ({
    key, original: child, value: sortKlvForDisplay(child), tag: tagFromDetailedKey(key),
  }));
  const compare = (a, b) => {
    if (a.tag !== null && b.tag !== null) return a.tag - b.tag || a.key.localeCompare(b.key);
    if (a.tag !== null) return -1;
    if (b.tag !== null) return 1;
    return a.key.localeCompare(b.key);
  };
  const ordered = entries.every((entry, index) => index === 0 || compare(entries[index - 1], entry) <= 0);
  if (ordered && entries.every((entry) => entry.original === entry.value)) return value;
  if (!ordered) entries.sort(compare);
  return Object.fromEntries(entries.map(({ key, value: child }) => [key, child]));
}
